import { useState } from 'react';

export default function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(0);
  const [activeFilters, setActiveFilters] = useState({
    grayscale: false,
    highContrast: false,
    negativeContrast: false,
    lightBg: false,
    linksUnderline: false,
    readableFont: false,
  });

  const applyAll = (filters, size) => {
    const el = document.getElementById('main-wrapper');
    if (!el) return;

    const filterParts = [];
    if (filters.grayscale) filterParts.push('grayscale(1)');
    if (filters.highContrast) filterParts.push('contrast(1.5)');
    if (filters.negativeContrast) filterParts.push('invert(1)');
    el.style.filter = filterParts.join(' ') || '';
    el.style.backgroundColor = filters.lightBg ? '#FFFFFF' : '';

    const sizePercent = 100 + size * 20;
    el.style.zoom = size === 0 ? '' : `${sizePercent}%`;

    if (filters.readableFont) {
      el.style.fontFamily = 'Arial, Helvetica, sans-serif';
      el.style.letterSpacing = '1px';
      el.style.wordSpacing = '3px';
      el.style.lineHeight = '1.8';
    } else {
      el.style.fontFamily = '';
      el.style.letterSpacing = '';
      el.style.wordSpacing = '';
      el.style.lineHeight = '';
    }

    const links = el.querySelectorAll('a');
    links.forEach(a => { a.style.textDecoration = filters.linksUnderline ? 'underline' : ''; });
  };

  const toggleFilter = (key) => {
    const next = { ...activeFilters, [key]: !activeFilters[key] };
    setActiveFilters(next);
    applyAll(next, fontSize);
  };

  const changeSize = (delta) => {
    const next = Math.max(-2, Math.min(3, fontSize + delta));
    setFontSize(next);
    applyAll(activeFilters, next);
  };

  const reset = () => {
    const cleared = { grayscale: false, highContrast: false, negativeContrast: false, lightBg: false, linksUnderline: false, readableFont: false };
    setActiveFilters(cleared);
    setFontSize(0);
    applyAll(cleared, 0);
  };

  const hasActive = fontSize !== 0 || Object.values(activeFilters).some(v => v);

  const options = [
    { key: 'inc', label: 'Increase Text', icon: 'fa-magnifying-glass-plus', action: () => changeSize(1) },
    { key: 'dec', label: 'Decrease Text', icon: 'fa-magnifying-glass-minus', action: () => changeSize(-1) },
    { key: 'grayscale', label: 'Grayscale', icon: 'fa-palette', toggle: true },
    { key: 'highContrast', label: 'High Contrast', icon: 'fa-circle-half-stroke', toggle: true },
    { key: 'negativeContrast', label: 'Negative Contrast', icon: 'fa-eye', toggle: true },
    { key: 'lightBg', label: 'Light Background', icon: 'fa-lightbulb', toggle: true },
    { key: 'linksUnderline', label: 'Links Underline', icon: 'fa-link', toggle: true },
    { key: 'readableFont', label: 'Readable Font', icon: 'fa-font', toggle: true },
  ];

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="accessibility-toggle"
        aria-label="Accessibility Tools"
        className={`fixed top-20 left-0 z-[60] text-white w-11 h-11 flex items-center justify-center rounded-r-xl shadow-lg transition-colors ${hasActive ? 'bg-[var(--ls-gold)]' : 'bg-[var(--ls-navy)] hover:bg-[var(--ls-gold)]'}`}
      >
        <i className="fa-solid fa-wheelchair text-sm"></i>
      </button>

      {isOpen && (
        <div data-testid="accessibility-panel" className="fixed top-20 left-12 z-[60] bg-white rounded-2xl shadow-2xl border border-[var(--ls-border)] w-56 py-3 px-2">
          <div className="flex justify-between items-center mb-2 px-2">
            <h3 className="font-bold text-xs text-[var(--ls-navy)]">Accessibility Tools</h3>
            <button onClick={() => setIsOpen(false)} aria-label="Close" className="text-[var(--ls-text-light)] hover:text-[var(--ls-navy)]">
              <i className="fa-solid fa-xmark text-sm"></i>
            </button>
          </div>
          {hasActive && (
            <button onClick={reset} className="w-full mb-2 px-2 py-1.5 text-xs rounded-lg bg-red-50 text-red-500 font-medium flex items-center gap-2 hover:bg-red-100">
              <i className="fa-solid fa-rotate-left w-4"></i> Reset All
            </button>
          )}
          <div className="space-y-0.5">
            {options.map(opt => {
              const isActive = opt.toggle ? activeFilters[opt.key] : false;
              return (
                <button
                  key={opt.key}
                  onClick={opt.toggle ? () => toggleFilter(opt.key) : opt.action}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded-lg flex items-center gap-2 ${isActive ? 'bg-[var(--ls-gold)]/15 text-[var(--ls-gold-dark)] font-medium' : 'hover:bg-[var(--ls-bg)] text-[var(--ls-text)]'}`}
                >
                  <i className={`fa-solid ${opt.icon} w-4 text-center`}></i> {opt.label}
                  {isActive && <i className="fa-solid fa-check ml-auto text-[var(--ls-gold)]"></i>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
